"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useImagePrefetch } from "@/hooks/useImagePrefetch";
import {
  type Product,
  getProductAnyId,
  getProductBrandLabel,
  getProductImageSources,
} from "@/lib/product-utils";
import { suggestAttributes, type AttributeCount } from "@/lib/shop-agent-client";

import type { ShopperFilters } from "@/lib/shopper-search";

import ProductCard from "./ProductCard";
import { ProductGridSkeleton } from "./ProductGridSkeleton";

const PRIORITY_ROWS = 2;
const INITIAL_PREFETCH_COUNT = 8; // Reduced to avoid overwhelming the browser
const PREFETCH_BATCH_SIZE = 6; // Smaller batches to prevent ERR_INSUFFICIENT_RESOURCES
const PREFETCH_ROOT_MARGIN = "800px 0px"; // Reduced to focus on images closer to viewport
// Quality parameter to match ProductImage component
const IMAGE_QUALITY = 70;

interface ProductGridProps {
  searchParams?: ShopperFilters;
  products?: Product[];
  showSavedLabel?: boolean;
  /**
   * How many columns to subtract from the normal layout (e.g., 1 when a side drawer reduces available width).
   * Must be a non-negative integer. Defaults to 0.
   */
  columnShift?: number;
  defaultCollectionId?: string;
  /** Optional existing thread/search ID to use for MLT (if on results page) */
  existingThreadId?: string;
  onAvailableBrandsChange?: (brands: string[]) => void;
  onInitialBrandStats?: (stats: { brand: string; count: number }[]) => void;
  onAvailableStylesChange?: (styles: string[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onProductsLoaded?: (count: number) => void;
  onProductsChange?: (products: Product[]) => void;
  // Attribute suggestions flow
  onAttributeSuggestionsChange?: (
    suggestions: Array<{ attribute: string; suggested_values: [string] }>
  ) => void;
  onAttributeSuggestionsLoadingChange?: (loading: boolean) => void;
  // When true, call suggest API. Only true for agent responses.
  triggerSuggestAPI?: boolean;
  skipFetch?: boolean; // If true, an empty products list is final rather than loading
  /** Rendered instead of the default message when a search finished with no products. */
  emptyState?: React.ReactNode;
  /** Callback invoked before navigating to PDP (for caching scroll position) */
  onBeforeNavigateToPDP?: () => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({
  searchParams,
  products: propProducts,
  showSavedLabel = false,
  columnShift = 0,
  defaultCollectionId,
  existingThreadId,
  onAvailableBrandsChange,
  onInitialBrandStats,
  onAvailableStylesChange,
  onLoadingChange,
  onProductsLoaded,
  onProductsChange,
  onAttributeSuggestionsChange,
  onAttributeSuggestionsLoadingChange,
  triggerSuggestAPI = false,
  skipFetch = false,
  emptyState,
  onBeforeNavigateToPDP,
}) => {
  const [products, setProducts] = useState<Product[]>(propProducts || []);
  const gridRef = useRef<HTMLDivElement>(null);
  const prefetchSentinelRef = useRef<HTMLDivElement | null>(null);
  const nextPrefetchIndexRef = useRef<number>(0);
  const { prefetchNextImageUrls /* , getOptimalImageWidth */ } = useImagePrefetch();
  const brandStatsSentRef = useRef(false);
  // Stable refs for suggestion callbacks to avoid effect churn
  const attrSuggestChangeRef = useRef<
    ((suggestions: Array<{ attribute: string; suggested_values: [string] }>) => void) | undefined
  >(onAttributeSuggestionsChange);
  const attrSuggestLoadingRef = useRef<((loading: boolean) => void) | undefined>(
    onAttributeSuggestionsLoadingChange
  );
  useEffect(() => {
    attrSuggestChangeRef.current = onAttributeSuggestionsChange;
  }, [onAttributeSuggestionsChange]);
  useEffect(() => {
    attrSuggestLoadingRef.current = onAttributeSuggestionsLoadingChange;
  }, [onAttributeSuggestionsLoadingChange]);

  // Notify parent when products change
  useEffect(() => {
    if (onProductsChange) {
      onProductsChange(products);
    }
  }, [products, onProductsChange]);

  // Loading state management
  // Start with loading true until the parent passes products
  const [isLoading, setIsLoading] = useState(!propProducts);
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState(false);

  // Reset grid state on route navigations to avoid stale gray overlay
  useEffect(() => {
    const handleVisibility = () => {
      // When coming back from a background tab or after fast navigation, ensure loading is not stuck
      setIsLoading(false);
      if (onLoadingChange) onLoadingChange(false);
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
    };
  }, [onLoadingChange, setIsLoading]);

  // Notify parent when loading state changes
  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isLoading);
    }
  }, [isLoading, onLoadingChange]);

  // Removed redundant initial loading notification - already handled by isLoading state effect

  // Render the products the parent passes. An empty list it may still fill
  // (skipFetch unset) shows as loading.
  useEffect(() => {
    const loaded = propProducts ?? [];
    setProducts(loaded);
    if (skipFetch || loaded.length > 0) {
      onProductsLoaded?.(loaded.length);
      setIsLoading(false);
      setHasInitiallyFetched(true);
    } else {
      setIsLoading(true);
      setHasInitiallyFetched(false);
    }
  }, [propProducts, skipFetch, onProductsLoaded]);

  // Emit available brands when products list changes
  const handleBrandsChange = useCallback(() => {
    if (!onAvailableBrandsChange) return;

    const brandSet = new Set<string>();
    for (const p of products) {
      const brand = getProductBrandLabel(p);
      if (brand.trim().length > 0) {
        brandSet.add(brand.trim().toLowerCase());
      }
    }
    const brands = Array.from(brandSet).sort((a, b) => a.localeCompare(b));
    onAvailableBrandsChange(brands);
  }, [products, onAvailableBrandsChange]);

  useEffect(() => {
    try {
      handleBrandsChange();
    } catch (e) {
      console.warn("Failed to compute available brands", e);
    }
  }, [handleBrandsChange]);

  // Emit available styles when products change
  const handleStylesChange = useCallback(() => {
    if (!onAvailableStylesChange) return;

    const stylesSet = new Set<string>();
    const styleCounts = new Map<string, number>();
    for (const p of products) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const styles: string[] = ((p as any).enrichment?.styles || []) as string[];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tags: string[] = ((p as any).enrichment?.tags || []) as string[];
      [...styles, ...tags].forEach((s) => {
        if (typeof s === "string" && s.trim().length > 0) {
          const key = s.trim().toLowerCase();
          stylesSet.add(key);
          styleCounts.set(key, (styleCounts.get(key) || 0) + 1);
        }
      });
    }
    // Order by frequency desc, then alpha; take top 20
    const stylesArr = Array.from(stylesSet)
      .sort((a, b) => (styleCounts.get(b) || 0) - (styleCounts.get(a) || 0) || a.localeCompare(b))
      .slice(0, 20);
    onAvailableStylesChange(stylesArr);
  }, [products, onAvailableStylesChange]);

  useEffect(() => {
    try {
      handleStylesChange();
    } catch (e) {
      console.warn("Failed to compute available styles", e);
    }
  }, [handleStylesChange]);

  // Emit initial brand stats once per mount
  useEffect(() => {
    try {
      if (!onInitialBrandStats) return;
      if (brandStatsSentRef.current) return;
      if (!products || products.length === 0) return;

      const counts = new Map<string, number>();
      for (const p of products) {
        const brand = getProductBrandLabel(p);
        if (brand.trim().length > 0) {
          const key = brand.trim().toLowerCase();
          counts.set(key, (counts.get(key) || 0) + 1);
        }
      }
      const stats = Array.from(counts.entries())
        .map(([brand, count]) => ({ brand, count }))
        .sort((a, b) => b.count - a.count || a.brand.localeCompare(b.brand));
      onInitialBrandStats(stats);
      brandStatsSentRef.current = true;
    } catch (e) {
      console.warn("Failed to compute initial brand stats", e);
    }
  }, [products, onInitialBrandStats]);

  // Compute attribute/value counts from results and call attribute-agent suggest endpoint
  useEffect(() => {
    // Guard: need products and callback
    const onChangeCb = attrSuggestChangeRef.current;
    if (!onChangeCb) return;
    if (!products || products.length === 0) {
      onChangeCb([]);
      return;
    }

    // Only call suggest API when explicitly triggered (agent responses)
    if (!triggerSuggestAPI) {
      return;
    }

    // Helper to safely extract enrichment.attributes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extractAttributes = (p: any): Record<string, string[]> => {
      const attrs = (p?.enrichment?.attributes || {}) as Record<string, unknown>;
      const result: Record<string, string[]> = {};
      Object.entries(attrs).forEach(([key, val]) => {
        if (Array.isArray(val)) {
          const cleaned = (val as unknown[])
            .map((v) =>
              typeof v === "string" || typeof v === "number" ? String(v).trim().toLowerCase() : ""
            )
            .filter((v) => v.length > 0);
          if (cleaned.length > 0) result[String(key).toLowerCase()] = cleaned;
        } else if (typeof val === "string" || typeof val === "number") {
          const v = String(val).trim().toLowerCase();
          if (v.length > 0) result[String(key).toLowerCase()] = [v];
        }
      });
      return result;
    };

    // Build counts map
    const counts = new Map<string, { attribute: string; value?: string; count: number }>();
    for (const p of products) {
      const attrs = extractAttributes(p);
      Object.entries(attrs).forEach(([attribute, values]) => {
        // Skip overly generic attributes
        if (
          attribute === "color" ||
          attribute.includes("size") ||
          attribute === "age" ||
          attribute === "age_group"
        )
          return;
        if (!Array.isArray(values) || values.length === 0) return;
        values.forEach((value) => {
          const key = `${attribute}|${value}`;
          const prev = counts.get(key) || { attribute, value, count: 0 };
          prev.count += 1;
          counts.set(key, prev);
        });
      });
    }

    // Sort by count desc and take top 20
    const top = Array.from(counts.values())
      .sort(
        (a, b) =>
          b.count - a.count ||
          String(a.attribute).localeCompare(String(b.attribute)) ||
          String(a.value || "").localeCompare(String(b.value || ""))
      )
      .slice(0, 20);

    if (top.length === 0) {
      onChangeCb([]);
      return;
    }

    // Call attribute-agent suggest endpoint
    let cancelled = false;
    (async () => {
      try {
        attrSuggestLoadingRef.current?.(true);
        const attributes: AttributeCount[] = top.map((t) => ({
          attribute: t.attribute,
          value: t.value || null,
          count: t.count,
        }));
        const items = await suggestAttributes(attributes);
        if (!cancelled) {
          onChangeCb(items as Array<{ attribute: string; suggested_values: [string] }>);
        }
      } catch {
        if (!cancelled) {
          // On error, clear suggestions
          onChangeCb([]);
        }
      } finally {
        if (!cancelled) attrSuggestLoadingRef.current?.(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [products, triggerSuggestAPI]);

  // Filter out products without images for downstream rendering and prefetch work
  const imageProducts = useMemo(() => {
    return products.filter((product) => {
      if (!product) return false;
      return getProductImageSources(product).length > 0;
    });
  }, [products]);

  // Distribute products with images into columns for different screen sizes
  const distributeIntoColumns = (columnCount: number) => {
    const columns: Product[][] = Array.from({ length: columnCount }, () => []);

    imageProducts.forEach((product, index) => {
      const columnIndex = index % columnCount;
      columns[columnIndex].push(product);
    });

    return columns;
  };

  // Helper to adjust column count, ensuring at least 1
  const adj = (base: number) => Math.max(1, base - columnShift);

  const prefetchUrl = useCallback(
    (url: string | null | undefined, _isPrimary: boolean = false, isVisible: boolean = false) => {
      if (!url) return;
      if (typeof window === "undefined") return;

      // Use the new hook with smart sizing
      prefetchNextImageUrls(url, {
        quality: IMAGE_QUALITY,
        priority: isVisible ? "high" : "low",
        // Only prefetch the optimal size for non-visible images
        onlyOptimalSize: !isVisible,
      });
    },
    [prefetchNextImageUrls]
  );

  const prefetchImages = useCallback(
    (startIndex: number, count: number = PREFETCH_BATCH_SIZE) => {
      if (typeof window === "undefined") return;
      if (imageProducts.length === 0) return;

      const endIndex = Math.min(imageProducts.length, startIndex + count);
      for (let index = startIndex; index < endIndex; index += 1) {
        const product = imageProducts[index];
        if (!product) continue;

        const sources = getProductImageSources(product);
        if (!sources || sources.length === 0) continue;

        // Determine if this product is in the initial visible area
        const isInInitialView = index < INITIAL_PREFETCH_COUNT;

        // Only prefetch the first image source to reduce load
        const primarySource = sources[0];
        if (primarySource?.primary) {
          prefetchUrl(primarySource.primary, true, isInInitialView);

          // Only prefetch fallback for visible images
          if (
            isInInitialView &&
            primarySource.fallback &&
            primarySource.fallback !== primarySource.primary
          ) {
            prefetchUrl(primarySource.fallback, false, false);
          }
        }
      }
    },
    [imageProducts, prefetchUrl]
  );

  useEffect(() => {
    // Reset prefetch index when products change
    const initialEnd = Math.min(imageProducts.length, INITIAL_PREFETCH_COUNT);
    nextPrefetchIndexRef.current = initialEnd;

    if (initialEnd > 0) {
      prefetchImages(0, initialEnd);
    }

    if (imageProducts.length > initialEnd) {
      // Prefetch next batch immediately for smoother scrolling
      prefetchImages(initialEnd);
      nextPrefetchIndexRef.current = Math.min(
        imageProducts.length,
        initialEnd + PREFETCH_BATCH_SIZE
      );
    }
  }, [imageProducts, prefetchImages, products.length]);

  // Cleanup handled by the useImagePrefetch hook

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sentinel = prefetchSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const startIndex = nextPrefetchIndexRef.current;
          if (startIndex >= imageProducts.length) return;

          prefetchImages(startIndex);
          nextPrefetchIndexRef.current = Math.min(
            imageProducts.length,
            startIndex + PREFETCH_BATCH_SIZE
          );
        });
      },
      {
        rootMargin: PREFETCH_ROOT_MARGIN,
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [imageProducts.length, prefetchImages]);

  // Show skeleton loading state when loading and no products yet
  // Figma: Product Card - Loading State (Node: 895:24115)
  if ((isLoading || !hasInitiallyFetched) && products.length === 0) {
    return <ProductGridSkeleton />;
  }

  // Show no-results message - only after initial fetch has completed
  if (products.length === 0 && !isLoading && hasInitiallyFetched) {
    if (!searchParams && !propProducts) {
      return (
        <div className="p-8 text-center">
          <p className="text-gray-600">
            No products available at the moment. Please try again later.
          </p>
        </div>
      );
    }

    if (emptyState) {
      return <>{emptyState}</>;
    }

    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">
          We couldn&apos;t find any products matching your request. Try clearing filters or ask the
          Shopping Assistant to broaden your search.
        </p>
      </div>
    );
  }

  return (
    <div ref={gridRef} data-testid="product-grid" className="w-full overflow-hidden">
      {/* Up to extra-large screens (<1280px) – 2 columns */}
      <div className="flex w-full gap-3 xl:hidden">
        {distributeIntoColumns(adj(2)).map((column, colIndex) => (
          <div key={`col-2-${colIndex}`} className="min-w-0 flex-1 space-y-3">
            {column.map((product, index) => {
              const productKey = getProductAnyId(product);
              return (
                <ProductCard
                  compact={columnShift > 0}
                  key={`2col-${productKey !== "unknown" ? productKey : `idx-${index}`}`}
                  product={product}
                  showSavedLabel={showSavedLabel}
                  defaultCollectionId={defaultCollectionId}
                  existingThreadId={existingThreadId}
                  priorityFirst={index < PRIORITY_ROWS && colIndex === 0}
                  onBeforeNavigateToPDP={onBeforeNavigateToPDP}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Extra-large screens (1280px–1535px) – 3 columns */}
      <div className="hidden w-full gap-3 xl:flex 2xl:hidden">
        {distributeIntoColumns(adj(3)).map((column, colIndex) => (
          <div key={`col-3-${colIndex}`} className="min-w-0 flex-1 space-y-3">
            {column.map((product, index) => {
              const productKey = getProductAnyId(product);
              return (
                <ProductCard
                  compact={columnShift > 0}
                  key={`3col-${productKey !== "unknown" ? productKey : `idx-${index}`}`}
                  product={product}
                  showSavedLabel={showSavedLabel}
                  defaultCollectionId={defaultCollectionId}
                  existingThreadId={existingThreadId}
                  priorityFirst={index < PRIORITY_ROWS && colIndex === 0}
                  onBeforeNavigateToPDP={onBeforeNavigateToPDP}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* 2XL screens (1536px–1919px) – 4 columns */}
      <div className="hidden w-full gap-3 2xl:flex 3xl:hidden">
        {distributeIntoColumns(adj(4)).map((column, colIndex) => (
          <div key={`col-4-${colIndex}`} className="min-w-0 flex-1 space-y-3">
            {column.map((product, index) => {
              const productKey = getProductAnyId(product);
              return (
                <ProductCard
                  compact={columnShift > 0}
                  key={`4col-${productKey !== "unknown" ? productKey : `idx-${index}`}`}
                  product={product}
                  showSavedLabel={showSavedLabel}
                  defaultCollectionId={defaultCollectionId}
                  existingThreadId={existingThreadId}
                  priorityFirst={index < PRIORITY_ROWS && colIndex === 0}
                  onBeforeNavigateToPDP={onBeforeNavigateToPDP}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* 3XL screens (≥1920px) – 5 columns */}
      <div className="hidden w-full gap-3 3xl:flex">
        {distributeIntoColumns(adj(5)).map((column, colIndex) => (
          <div key={`col-5-${colIndex}`} className="min-w-0 flex-1 space-y-3">
            {column.map((product, index) => {
              const productKey = getProductAnyId(product);
              return (
                <ProductCard
                  compact={columnShift > 0}
                  key={`5col-${productKey !== "unknown" ? productKey : `idx-${index}`}`}
                  product={product}
                  showSavedLabel={showSavedLabel}
                  defaultCollectionId={defaultCollectionId}
                  existingThreadId={existingThreadId}
                  priorityFirst={index < PRIORITY_ROWS && colIndex === 0}
                  onBeforeNavigateToPDP={onBeforeNavigateToPDP}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div ref={prefetchSentinelRef} aria-hidden="true" style={{ height: "1px", width: "100%" }} />
    </div>
  );
};

export default ProductGrid;
